<template>
  <component
    :is="element"
    class="button"
    :class="[
      $blockClass(blockClass, 'button'),
      modifierClasses,
      {
        [$class('active')]: showStateIcon,
        [$class('loading')]: loading,
        'button--has-hover-icon': $slots.hoverIcon,
        'button--has-text-hover-icon': $slots.textHoverIcon,
        'button--add-to-cart': addToCartButton,
        [`button--validation-${validationState}`]: validationState
      },
    ]"
    :disabled="loading || validationState === 'error'"
    :href="url || null"
    :type="type || null"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <div class="button__pre-content">
      <span
        v-if="showTextIcon"
        class="button__icon"
        :class="$blockClass(blockClass, 'button-icon')"
      >
        <slot name="textIcon" />
      </span>

      <span
        class="button__label"
        :class="[
          $blockClass(blockClass, 'button-label'),
          { 'button__label--no-icon': !showIcon },
        ]"
        v-html="labelText"
      />
    </div>

    <span
      v-if="buttonMessage"
      class="button__message"
      :class="{
        [$class('active')]: messageIsHovered
      }"
      @mouseenter="messageIsHovered = true"
      @mouseleave="messageIsHovered = false"
      @click="(event) => handleMessageClick(event)"
      v-html="buttonMessage"
    />

    <span
      v-if="showIcon"
      class="button__icon"
      :class="$blockClass(blockClass, 'button-icon')"
    >
      <slot name="icon" />

      <span
        v-if="$slots.hoverIcon"
        class="button__hover-icon"
        :class="$blockClass(blockClass, 'button-hover-icon')"
      >
        <slot name="hoverIcon" />
      </span>
    </span>

    <span
      v-if="showStateIcon && $slots.stateIcon"
      class="button__state"
      :class="$blockClass(blockClass, 'button-state')"
    >
      <slot name="stateIcon" />
    </span>
  </component>
</template>

<script>

/**
 * Vue: Button (btn)
 * -----------------------------------------------------------------------------
 * Global interactive button element.
 * - Styles are imported in critical styles.
 * - Must be imported as <btn> as <button> is a default element.
 *
 * @param {Boolean} [addToCartButton] - Indicates if btn is used for atc.
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {String} [buttonMessage] - Additional message to display within the
 * button element.
 * @param {String} [customElement] - Optional override to set button element.
 * @param {String} [hoverText] - Text to be displayed in place of the label on
 * hover.
 * @param {String} label - Button label.
 * @param {String} loading - Indicates if the button is in its loading state.
 * @param {String} [modifiers] - Button modifiers in space separated list
 * (secondary, light, outlined, text, block, reversed, or large).
 * @param {Boolean} [showIcon] - Whether to show the button icon.
 * @param {Boolean} [showTextIcon] - Whether to show the text button icon.
 * @param {Boolean} [showStateIcon] - Show secondary button state, replaces text
 * and default icon with `stateIcon` slot.
 * @param {String} [type] - Type of button (button or submit).
 * @param {String} [url] - URL if link.
 * @param {String} [validationState] - Defines if the button should display a
 * validation state.
 *
 */
import IconChevronRightSpecial from '~icons/direction/arrow/right-center.svg'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Btn',

  components: {
    IconChevronRightSpecial,
  },

  props: {
    addToCartButton: {
      type: Boolean,
      default: false,
    },
    blockClass: {
      type: String,
      default: '',
    },
    buttonMessage: {
      type: String,
      default: '',
    },
    customElement: {
      type: String,
      default: '',
    },
    hoverText: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    modifiers: {
      type: [String, Array],
      default: '',
    },
    showIcon: {
      type: Boolean,
      default: true,
    },
    showTextIcon: {
      type: Boolean,
      default: true,
    },
    showStateIcon: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: '',
      validator: (value) => {
        return ['', 'button', 'submit'].includes(value)
      },
    },
    url: {
      type: String,
      default: null,
    },
    validationState: {
      type: String,
      default: '',
      validator: (value) => {
        return ['', 'pending', 'error', 'success'].includes(value)
      },
    },
    validationText: {
      type: String,
      default: '',
    },
  },

  emits: {
    'message-hovered': (payload) => typeof payload === 'boolean',
    'message-clicked': (payload) => typeof payload === 'boolean',
  },

  data() {
    return {
      cnvsGlobal: true,
      isHovered: false,
      messageIsHovered: false,
    }
  },

  computed: {

    /**
     * Computes the text to display for the button label.
     * @returns {String}
     */
    labelText() {
      if (this.validationText) {
        return this.validationText
      }

      if (!this.hoverText || !this.addToCartButton) {
        return this.label
      }

      return this.isHovered ? this.hoverText : this.label
    },

    /**
     * Compute element type.
     * @returns {String}
     */
    element() {
      if (this.customElement) {
        return this.customElement
      }

      return this.url ? 'a' : 'button'
    },

    /**
     * Compute modifier classes.
     * @returns {String}
     */
    modifierClasses() {
      if (!this.modifiers) {
        return ''
      }

      let modifiers = []

      if (Array.isArray(this.modifiers)) {
        modifiers = this.modifiers
      } else {
        modifiers = this.modifiers.split(/[\s,]/g)
      }

      return modifiers.map((modifier) => {
        return `button--${modifier}`
      }).join(' ')
    },
  },

  watch: {

    /**
     * Emit an event when we hover the message, so that a parent can react.
     */
    messageIsHovered(value) {
      this.$emit('message-hovered', value)
    },
  },

  methods: {

    /**
     * Emits an event when the button message is clicked.
     */
    handleMessageClick(event) {
      if (!this.mobileMessageClickActive) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      this.$emit('message-clicked', true)
    },
  },
}
</script>
